/**
 * 034 T016 — what a failed request throws, replacing Angular's `HttpErrorResponse`.
 *
 * ONE definition, in one file, because `toApiError` narrows on it with `instanceof` and the HTTP
 * client constructs it: two shapes of "a request failed" would drift the moment one of them gained
 * a field, and the reader of `api-error.ts` would have no way to tell which one reached it.
 *
 * It carries exactly the four members `api-error.ts` actually reads — `status`, `error`,
 * `statusText`, `url` — and nothing else. Angular's class carried a dozen; copying them across
 * would have been importing a shape rather than porting a need.
 *
 * `extends Error` so that an unhandled one still produces a usable stack, which is the property a
 * plain object would silently cost.
 */
export class HttpFailure extends Error {
  /** 0 when the request never reached the API — a network failure, a dead proxy, an aborted fetch. */
  readonly status: number;
  /** The parsed body when there was one: a problem document, a string, a Blob, or null. */
  readonly error: unknown;
  readonly statusText: string;
  readonly url: string | null;

  constructor(init: { status: number; error?: unknown; statusText?: string; url?: string | null }) {
    super(`HTTP ${init.status}${init.statusText ? ` ${init.statusText}` : ''}`);
    this.name = 'HttpFailure';
    this.status = init.status;
    this.error = init.error ?? null;
    this.statusText = init.statusText ?? '';
    this.url = init.url ?? null;
  }
}
